import type { Transition, Variants } from 'framer-motion';

/**
 * Shared motion vocabulary for the Conversations inbox.
 *
 * This is a *calm* system on purpose — a chat UI is read constantly, so
 * motion here is measured in single-digit pixels and sub-300ms durations.
 * Anything louder reads as gimmicky the tenth time you switch conversations.
 *
 * Global reduced-motion handling lives at the page root via
 * `<MotionConfig reducedMotion="user">`, so every variant below can stay
 * declarative — no manual `prefers-reduced-motion` branching per component.
 */
export const EASE: Transition['ease'] = [0.16, 1, 0.3, 1];
export const SPRING_TRANSITION: Transition = {
  type: 'spring',
  stiffness: 380,
  damping: 30,
};

/** Wraps the conversation list so rows animate in smoothly without heavy stagger blinking. */
export const LIST_CONTAINER: Variants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.02 },
  },
};

/** Single conversation row — smooth quick fade. */
export const LIST_ROW: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { duration: 0.15, ease: 'easeOut' },
  },
};

/** Message bubble entrance — slides in dynamically with spring. */
export const bubbleVariants = (isOutbound: boolean): Variants => ({
  hidden: { opacity: 0, y: 10, x: isOutbound ? 14 : -14, scale: 0.96 },
  show: {
    opacity: 1,
    y: 0,
    x: 0,
    scale: 1,
    transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1] },
  },
});

/** Wraps a message list so bubbles stagger in lightly on conversation open. */
export const MESSAGE_CONTAINER: Variants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.05, delayChildren: 0.04 },
  },
};

/** Gentle breathing pulse for unread-count badges. Collapses to a no-op
 *  automatically under reduced motion via MotionConfig. */
export const UNREAD_PULSE = {
  scale: [1, 1.15, 1],
};
export const UNREAD_PULSE_TRANSITION: Transition = {
  duration: 1.8,
  repeat: Infinity,
  ease: 'easeInOut',
  repeatDelay: 1.2,
};

/** Panel-level cross-fade used when switching the selected conversation. */
export const PANEL_FADE: Variants = {
  hidden: { opacity: 0, y: 12, scale: 0.99 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.28, ease: [0.16, 1, 0.3, 1] },
  },
  exit: {
    opacity: 0,
    y: -8,
    scale: 0.99,
    transition: { duration: 0.18, ease: [0.16, 1, 0.3, 1] },
  },
};
